import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, Link } from "@mui/material";
import MotionInfo from "../../Icons/MotionInfo";
import MotionCheck from "../../Icons/MotionCheck";
import { styled } from "@mui/material/styles";
import ActionRequiredIcon from "../../Icons/ActionRequiredIcon";
import MotionCautionIcon from "../../Icons/MotionCautionIcon";
import EMPButton from "../EMPButton/EMPButton";

const getBackgroundColor = (theme, severity) => {
  if (severity === "success") {
    return theme.palette.success.main;
  }

  if (severity === "error") {
    return theme.palette.error.main;
  }

  if (severity === "warning") {
    return theme.palette.warning.main;
  }

  return theme.palette.info.main;
};

const getAlertIcon = (severity) => {
  if (severity === "success") {
    return <MotionCheck htmlColor="#008200" />;
  }

  if (severity === "error") {
    return <ActionRequiredIcon htmlColor="#d32f2f" />;
  }

  if (severity === "warning") {
    return <MotionCautionIcon htmlColor="#B16201" />;
  }

  return <MotionInfo />;
};

const BannerContainer = styled(Box)(({ theme, severity }) => ({
  width: "100%",
  boxSizing: "border-box",
  display: "flex",
  alignItems: "flex-start",
  borderRadius: "8px",
  backgroundColor: getBackgroundColor(theme, severity),
}));

const IconBox = styled(Box)({
  flexShrink: 0,
  width: "24px",
  height: "24px",
  lineHeight: 0,
  marginTop: "16px",
  marginLeft: "16px",
  "& svg": {
    width: "24px",
    height: "24px",
    display: "block",
  },
});

const EMPBanner = ({
  title,
  content,
  hasLink,
  linkText,
  href,
  severity,
  actionButton,
  icon,
}) => {
  const alertIcon = icon || getAlertIcon(severity);

  return (
    <BannerContainer severity={severity} role="alert">
      <IconBox>{alertIcon}</IconBox>
      <Box
        sx={{
          flex: 1,
          pt: "16px",
          pb: "16px",
          pl: "12px",
          minWidth: 0,
        }}
      >
        <Typography
          variant="subtitle1"
          component="p"
          sx={{
            margin: 0,
            padding: 0,
            fontWeight: 700,
            lineHeight: "24px",
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="body1"
          color="text.primary"
          sx={{ display: "inline" }}
        >
          {content}{" "}
        </Typography>

        {hasLink && (
          <Link
            variant="body1"
            href={href}
            underline="always"
            target="_blank"
            rel="noreferrer"
            color="text.primary"
          >
            {linkText}
          </Link>
        )}
      </Box>

      {actionButton && (
        <Box
          sx={{
            flexShrink: 0,
            pt: "20px",
            pb: "20px",
            pr: "32px",
            pl: "16px",
            ml: "auto",
            display: "flex",
            alignItems: "center",
          }}
        >
          <EMPButton
            variant="contained"
            color="dark"
            onClick={actionButton.onClick}
            testId={actionButton.testId}
            sx={{
              backgroundColor: "#000000",
              color: "#FFFFFF",
              borderRadius: "999px",
              padding: "10px 20px",
              whiteSpace: "nowrap",
              "&:hover": {
                backgroundColor: "#1A1A1A",
              },
            }}
          >
            {actionButton.text}
          </EMPButton>
        </Box>
      )}
    </BannerContainer>
  );
};

EMPBanner.propTypes = {
  title: PropTypes.node.isRequired,
  content: PropTypes.node.isRequired,
  hasLink: PropTypes.bool,
  linkText: PropTypes.node,
  href: PropTypes.string,
  severity: PropTypes.oneOf(["success", "error", "warning", "info"]),
  actionButton: PropTypes.shape({
    text: PropTypes.node.isRequired,
    onClick: PropTypes.func.isRequired,
    testId: PropTypes.string,
  }),
  icon: PropTypes.node,
};

EMPBanner.defaultProps = {
  hasLink: false,
  linkText: null,
  href: undefined,
  severity: "info",
  actionButton: null,
  icon: null,
};

export default EMPBanner;
